import type { LocalDistanceOption, LocalTargetMode } from './LocalPublishOptions'

export function toRadiusKm(distance: LocalDistanceOption): number | null {
  if (distance === 'city') return null
  return Math.round(Number(distance) * 1.60934)
}

export function parseCityLabel(label: string): { city: string; region: string; country: string } {
  const raw = String(label || '').trim()
  if (!raw) return { city: '', region: '', country: '' }
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean)
  if (parts.length <= 1) return { city: raw, region: '', country: '' }
  const city = parts[0]
  const second = parts[1] || ''
  const third = parts[2] || ''
  const country = third && third.length === 2 ? third.toUpperCase() : ''
  const region = second
  return { city, region, country }
}

const REGION_ONLY_LABELS = new Set([
  'alabama', 'alaska', 'arizona', 'arkansas', 'california', 'colorado', 'connecticut', 'delaware',
  'florida', 'georgia', 'hawaii', 'idaho', 'illinois', 'indiana', 'iowa', 'kansas', 'kentucky',
  'louisiana', 'maine', 'maryland', 'massachusetts', 'michigan', 'minnesota', 'mississippi',
  'missouri', 'montana', 'nebraska', 'nevada', 'new hampshire', 'new jersey', 'new mexico',
  'new york', 'north carolina', 'north dakota', 'ohio', 'oklahoma', 'oregon', 'pennsylvania',
  'rhode island', 'south carolina', 'south dakota', 'tennessee', 'texas', 'utah', 'vermont',
  'virginia', 'washington', 'west virginia', 'wisconsin', 'wyoming',
  'dc', 'ca', 'ny', 'tx', 'fl',
])

export function normalizeProfileCityLabel(label: string): string {
  const raw = String(label || '').trim()
  if (!raw) return ''
  const normalized = raw.toLowerCase()
  if (!raw.includes(',') && REGION_ONLY_LABELS.has(normalized)) return ''
  return raw
}

export function resolveGeoDeniedFallback(profileCityLabel: string): {
  mode: LocalTargetMode
  cityLabel: string
  focusCustomCityInput: boolean
} {
  const city = normalizeProfileCityLabel(profileCityLabel)
  if (city) {
    return { mode: 'profile_city', cityLabel: city, focusCustomCityInput: false }
  }
  return { mode: 'custom_city', cityLabel: '', focusCustomCityInput: true }
}

export function buildLocalPayload(input: {
  enabled: boolean
  mode: LocalTargetMode
  distance: LocalDistanceOption
  cityLabel: string
  lat: number | null
  lng: number | null
}) {
  const radiusKm = input.enabled ? toRadiusKm(input.distance) : null
  const parsed = parseCityLabel(input.cityLabel)
  const hasCoords = input.lat != null && input.lng != null
  if (!input.enabled) {
    return {
      is_local: false,
      local_city: '',
      local_region: '',
      local_country: '',
      local_lat: null,
      local_lng: null,
      local_radius_km: null,
    }
  }
  if (input.mode === 'near_me') {
    return {
      is_local: true,
      local_city: parsed.city,
      local_region: parsed.region,
      local_country: parsed.country,
      local_lat: hasCoords ? input.lat : null,
      local_lng: hasCoords ? input.lng : null,
      local_radius_km: radiusKm,
    }
  }
  return {
    is_local: true,
    local_city: parsed.city,
    local_region: parsed.region,
    local_country: parsed.country,
    local_lat: null,
    local_lng: null,
    local_radius_km: radiusKm,
  }
}
